// src/scene/postfx.ts
//
// P0 — cinematic post-processing pipeline. The scene has no real lights; mood
// comes from baked shader tone + post. This composer makes the white wall glow
// softly out of the dark (bloom on the genuinely bright hotspots only — NEVER
// hazing the near-white artwork mats), pools attention with a screen-space
// vignette, and lays a faint film grain over the frame so the darks aren't a
// dead flat black.
//
// Pipeline:
//   RenderPass(scene, camera)
//   UnrealBloomPass        — glow on hotspots only (tuned threshold/strength)
//   BokehPass (disabled)   — P2 depth-of-field, toggled on only during a dive
//   vignette+grain ShaderPass
//   OutputPass             — ACES tone-map + sRGB, last
//
// HalfFloatType targets keep bloom working in HDR (the wall plaster sits above
// ~0.9 in linear, so a threshold near 0.82 catches the warm pool centres and
// near-white highlights without lifting the mats). The ink-dive overlay is
// composited by transitions.render() AFTER composer.render() with
// autoClear=false, exactly as before — the composer leaves the default
// framebuffer holding the final frame, so the overlay still draws on top.

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { BokehPass } from 'three/addons/postprocessing/BokehPass.js';

// --- Tuned constants (the #1 acceptance criterion lives here) -------------
// threshold high enough that the ivory mats (~0.86 linear after the panel
// shader, art textures lower) DON'T bloom — only the warm pool centres,
// cornice/cove glow and specular floor streak (which sit brighter) bleed.
export const BLOOM_THRESHOLD = 0.9;   // above the ivory mats so they don't bloom-haze
export const BLOOM_STRENGTH = 0.16;   // gentle glow only on the brightest hotspots
export const BLOOM_RADIUS = 0.6;
export const EXPOSURE = 0.98;

/** Combined vignette + film-grain pass. Replaces room.ts's vignette plane so we
 *  never double-vignette. Grain is animated by uTime and is intentionally tiny
 *  in the brights, a touch stronger in the shadows (where banding shows). */
const VignetteGrainShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uAspect: { value: 1 },
    uVignette: { value: 0.55 },   // corner darkening strength
    uGrain: { value: 0.05 },      // grain amplitude
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uAspect;
    uniform float uVignette;
    uniform float uGrain;
    varying vec2 vUv;
    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    void main() {
      vec4 tex = texture2D( tDiffuse, vUv );
      // vignette: circular falloff (aspect-corrected), clear centre → dark corners
      vec2 q = vUv - 0.5;
      q.x *= uAspect;
      float d = length(q);
      float vig = 1.0 - smoothstep(0.42, 0.92, d) * uVignette;
      vec3 col = tex.rgb * vig;
      // film grain: animated value noise, stronger in shadow, fading out of brights
      float n = hash(vUv * vec2(uAspect, 1.0) * 900.0 + fract(uTime) * 100.0) - 0.5;
      float luma = dot(col, vec3(0.299, 0.587, 0.114));
      float shadowWeight = 1.0 - smoothstep(0.0, 0.55, luma);
      col += n * uGrain * (0.4 + 0.6 * shadowWeight);
      gl_FragColor = vec4(col, tex.a);
    }`,
};

export interface PostFX {
  composer: EffectComposer;
  /** Per-frame: advance grain time. dt in seconds. */
  update(dt: number): void;
  render(): void;
  resize(): void;
  /** P2 DoF: focus the hall blur on a point `dist` world-units ahead of the
   *  camera (the dived work). Engages the BokehPass. */
  enterDof(dist: number): void;
  /** P2 DoF: disengage on Esc/return — the hall snaps back to crisp. */
  exitDof(): void;
}

export function createPostFX(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
): PostFX {
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = EXPOSURE;

  const dpr = renderer.getPixelRatio();
  const size = renderer.getSize(new THREE.Vector2());

  const composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(
    size.x * dpr, size.y * dpr,
    { type: THREE.HalfFloatType },   // HDR so bloom has headroom
  ));
  composer.setPixelRatio(dpr);
  composer.setSize(size.x, size.y);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const bloom = new UnrealBloomPass(
    new THREE.Vector2(size.x, size.y),
    BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD,
  );
  composer.addPass(bloom);

  // P2 depth-of-field — built once, disabled until a dive. focus is the world
  // distance to the focal plane along the camera's look direction.
  const bokeh = new BokehPass(scene, camera, {
    focus: 4.6, aperture: 0.00018, maxblur: 0.008,
  });
  bokeh.enabled = false;
  composer.addPass(bokeh);

  const vignetteGrain = new ShaderPass(VignetteGrainShader);
  composer.addPass(vignetteGrain);

  const outputPass = new OutputPass();   // ACES tone-map + sRGB encode, last
  composer.addPass(outputPass);

  function applyAspect(): void {
    vignetteGrain.uniforms.uAspect.value =
      window.innerWidth / Math.max(1, window.innerHeight);
  }
  applyAspect();

  let dofTween = 0;       // eased 0..1 blur engage, for a soft DoF ramp
  let dofTarget = 0;

  return {
    composer,
    update(dt: number): void {
      vignetteGrain.uniforms.uTime.value += dt;
      // ease the bokeh aperture/maxblur in and out so DoF ramps, not snaps
      const speed = Math.min(1, dt * 6);
      dofTween += (dofTarget - dofTween) * speed;
      if (dofTween < 0.001 && dofTarget === 0) {
        bokeh.enabled = false;
      } else {
        bokeh.enabled = true;
        bokeh.materialBokeh.uniforms['maxblur'].value = 0.009 * dofTween;
        bokeh.materialBokeh.uniforms['aperture'].value = 0.00022 * dofTween;
      }
    },
    render(): void { composer.render(); },
    resize(): void {
      const w = window.innerWidth, h = window.innerHeight;
      composer.setSize(w, h);
      bloom.setSize(w, h);
      applyAspect();
    },
    enterDof(dist: number): void {
      bokeh.materialBokeh.uniforms['focus'].value = dist;
      dofTarget = 1;
      bokeh.enabled = true;
    },
    exitDof(): void {
      dofTarget = 0;
    },
  };
}
